// per specs/060-agreed-alerts/plan.md C1, C2, C4
//
// The watched medicine group (TF-127, TF-128): one object the System
// Administrator defines once, which A4, A5 and A10 read by reference. A change to
// the group reaches every rule that names it, with no rule edit.
//
// These are the HTTP shapes of `/api/v1/admin/watched-groups` (C4). The bff
// validates every body against the schemas below; the catalog re-checks the same
// bounds, because the database is the last line and gRPC has other callers.
import { z } from 'zod';
import { UUID_PATTERN } from './domain';

/**
 * The group the catalog migration seeds, "Medicamente urmărite", with no members
 * and no threshold. The seeded A4, A5 and A10 rules point at it, so the id is a
 * constant both migrations share rather than a value either one generates.
 */
export const WATCHED_GROUP_DEFAULT_ID = 'f3a9c2d1-5b7e-4c60-9a12-060a0e000001';
export const WATCHED_GROUP_DEFAULT_NAME = 'Medicamente urmărite';

/** QUANTITY: units of national stock. PERCENT: of the stock 30 days earlier (A5). */
export const WATCHED_GROUP_THRESHOLD_KINDS = ['QUANTITY', 'PERCENT'] as const;
export type WatchedGroupThresholdKind = (typeof WATCHED_GROUP_THRESHOLD_KINDS)[number];

/**
 * MEDICINE: one catalogue medicine (a DC) and every code filed under it.
 * DCI_DOSE_FORM: every code whose DCI, dose and form match, across producers.
 */
export const WATCHED_GROUP_MEMBER_KINDS = ['MEDICINE', 'DCI_DOSE_FORM'] as const;
export type WatchedGroupMemberKind = (typeof WATCHED_GROUP_MEMBER_KINDS)[number];

/** What one history row records. Member edits are rows of the group's own trail. */
export const WATCHED_GROUP_CHANGE_TYPES = [
  'CREATED',
  'UPDATED',
  'DELETED',
  'MEMBER_ADDED',
  'MEMBER_REMOVED',
] as const;
export type WatchedGroupChangeType = (typeof WATCHED_GROUP_CHANGE_TYPES)[number];

/** The column bounds of C1, stated once for the bff, the catalog and the form. */
export const WATCHED_GROUP_LIMITS = {
  nameMax: 120,
  descriptionMax: 2000,
  dciMax: 255,
  doseMax: 100,
  formMax: 300,
  // numeric(14,3): eleven integer digits, three decimals.
  thresholdMax: 99_999_999_999.999,
  thresholdDecimals: 3,
  percentMax: 100,
  dciOptionsMax: 50,
} as const;

/**
 * Every refusal the group endpoints answer with, beside the generic ones
 * (IDENTIFIER_INVALID, SYSTEM_ADMIN_REQUIRED). The FrontOffice translates each.
 */
export const WATCHED_GROUP_ERROR_CODES = [
  'WATCHED_GROUP_NOT_FOUND',
  'WATCHED_GROUP_NAME_REQUIRED',
  'WATCHED_GROUP_NAME_TOO_LONG',
  'WATCHED_GROUP_NAME_TAKEN',
  'WATCHED_GROUP_DESCRIPTION_TOO_LONG',
  'WATCHED_GROUP_THRESHOLD_INVALID',
  'WATCHED_GROUP_MEMBER_INVALID',
  'WATCHED_GROUP_MEMBER_EXISTS',
  'WATCHED_GROUP_MEMBER_NOT_FOUND',
  'WATCHED_GROUP_MEDICINE_NOT_FOUND',
  'WATCHED_GROUP_IN_USE',
  'WATCHED_GROUP_BODY_INVALID',
] as const;
export type WatchedGroupErrorCode = (typeof WATCHED_GROUP_ERROR_CODES)[number];

const uuid = z.string().regex(UUID_PATTERN, 'must be a UUID');

const thresholdValue = z
  .number()
  .finite()
  .positive()
  .max(WATCHED_GROUP_LIMITS.thresholdMax)
  .refine((value) => hasAtMostDecimals(value, WATCHED_GROUP_LIMITS.thresholdDecimals), {
    message: `at most ${WATCHED_GROUP_LIMITS.thresholdDecimals} decimals`,
  });

/**
 * A threshold is both halves or neither (C1's CHECK), and a percentage is at most
 * 100. `null` on both says "no threshold"; A5 and A10 then refuse to save over the
 * group (TF-098).
 */
export const watchedGroupThresholdSchema = z
  .object({
    thresholdKind: z.enum(WATCHED_GROUP_THRESHOLD_KINDS).nullable(),
    thresholdValue: thresholdValue.nullable(),
  })
  .superRefine(thresholdPairIssues);

// Both halves or neither — an omitted half reads as an absent one — and a
// percentage of at most 100. The issue sits on `thresholdValue`, so a form can put
// it beside the field.
function thresholdPairIssues(
  t: {
    thresholdKind?: WatchedGroupThresholdKind | null | undefined;
    thresholdValue?: number | null | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  const kind = t.thresholdKind ?? null;
  const value = t.thresholdValue ?? null;
  if ((kind === null) !== (value === null)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['thresholdValue'],
      message: 'thresholdKind and thresholdValue are set together or not at all',
    });
  }
  if (kind === 'PERCENT' && value !== null && value > WATCHED_GROUP_LIMITS.percentMax) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['thresholdValue'],
      message: `a percentage threshold is at most ${WATCHED_GROUP_LIMITS.percentMax}`,
    });
  }
}

const name = z.string().trim().min(1).max(WATCHED_GROUP_LIMITS.nameMax);
const description = z.string().trim().max(WATCHED_GROUP_LIMITS.descriptionMax).nullable();

/** `POST /` */
export const createWatchedGroupBodySchema = z
  .object({
    name,
    description: description.optional(),
    thresholdKind: z.enum(WATCHED_GROUP_THRESHOLD_KINDS).nullable().optional(),
    thresholdValue: thresholdValue.nullable().optional(),
    active: z.boolean().optional(),
  })
  .strict()
  .superRefine(thresholdPairIssues);
export type CreateWatchedGroupBody = z.infer<typeof createWatchedGroupBodySchema>;

/**
 * `PATCH /:id`. Only what the body names changes. The threshold is changed as a
 * pair: name both halves (both `null` removes it), or neither.
 */
export const updateWatchedGroupBodySchema = z
  .object({
    name: name.optional(),
    description: description.optional(),
    thresholdKind: z.enum(WATCHED_GROUP_THRESHOLD_KINDS).nullable().optional(),
    thresholdValue: thresholdValue.nullable().optional(),
    active: z.boolean().optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    if (Object.keys(body).length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'nothing to update' });
      return;
    }
    if ((body.thresholdKind === undefined) !== (body.thresholdValue === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['thresholdValue'],
        message: 'thresholdKind and thresholdValue are changed together',
      });
      return;
    }
    if (body.thresholdKind !== undefined) {
      thresholdPairIssues(body, ctx);
    }
  });
export type UpdateWatchedGroupBody = z.infer<typeof updateWatchedGroupBodySchema>;

/**
 * The refusal code a body that failed its schema answers with: the field's own
 * where one fits, so the form can show it beside the field.
 */
export function watchedGroupBodyErrorCode(
  issue: Pick<z.ZodIssue, 'path' | 'code'>,
): WatchedGroupErrorCode {
  switch (issue.path[0]) {
    case 'name':
      return issue.code === 'too_big'
        ? 'WATCHED_GROUP_NAME_TOO_LONG'
        : 'WATCHED_GROUP_NAME_REQUIRED';
    case 'description':
      return 'WATCHED_GROUP_DESCRIPTION_TOO_LONG';
    case 'thresholdKind':
    case 'thresholdValue':
      return 'WATCHED_GROUP_THRESHOLD_INVALID';
    case 'kind':
    case 'medicineId':
    case 'dci':
    case 'dose':
    case 'form':
      return 'WATCHED_GROUP_MEMBER_INVALID';
    default:
      return 'WATCHED_GROUP_BODY_INVALID';
  }
}

/** `POST /:id/members` */
export const addWatchedGroupMemberBodySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('MEDICINE'), medicineId: uuid }).strict(),
  z
    .object({
      kind: z.literal('DCI_DOSE_FORM'),
      dci: z.string().trim().min(1).max(WATCHED_GROUP_LIMITS.dciMax),
      dose: z.string().trim().min(1).max(WATCHED_GROUP_LIMITS.doseMax),
      form: z.string().trim().min(1).max(WATCHED_GROUP_LIMITS.formMax),
    })
    .strict(),
]);
export type AddWatchedGroupMemberBody = z.infer<typeof addWatchedGroupMemberBodySchema>;

export interface WatchedGroupMemberDto {
  id: string;
  kind: WatchedGroupMemberKind;
  /** Set for MEDICINE, null for DCI_DOSE_FORM. */
  medicineId: string | null;
  /** Commercial name, form, dose and DCI of that medicine; null for DCI_DOSE_FORM. */
  medicineLabel: string | null;
  /** Set for DCI_DOSE_FORM, null for MEDICINE. */
  dci: string | null;
  dose: string | null;
  form: string | null;
  /** What the member is called on every surface: the medicine label, or DCI · dose · form. */
  label: string;
  createdAtIso: string;
}

/** One row of `GET /`. */
export interface WatchedGroupSummaryDto {
  id: string;
  name: string;
  description: string | null;
  thresholdKind: WatchedGroupThresholdKind | null;
  thresholdValue: number | null;
  active: boolean;
  memberCount: number;
  createdAtIso: string;
  /** Masked actor identifier, never a whole IDNP. */
  createdBy: string;
  updatedAtIso: string;
  updatedBy: string;
}

/** `GET /:id`, and the answer of every write. */
export interface WatchedGroupDto extends WatchedGroupSummaryDto {
  members: WatchedGroupMemberDto[];
}

export interface WatchedGroupFieldChangeDto {
  /** name | description | thresholdKind | thresholdValue | active | member */
  field: string;
  before: string;
  after: string;
}

/** One row of `GET /:id/history`, newest first. */
export interface WatchedGroupHistoryEntryDto {
  id: string;
  changeType: WatchedGroupChangeType;
  /** Masked actor identifier, never a whole IDNP. */
  actorId: string;
  actorRole: string;
  source: string;
  recordedAtIso: string;
  changes: WatchedGroupFieldChangeDto[];
}

/** One option of `GET /dci-options?q=`. */
export interface WatchedGroupDciOptionDto {
  dci: string;
  dose: string;
  form: string;
}

/** The text a member is shown and published under (C3 `member_label`). */
export function dciDoseFormLabel(parts: { dci: string; dose: string; form: string }): string {
  return [parts.dci, parts.form, parts.dose].filter((part) => part.length > 0).join(' · ');
}

function hasAtMostDecimals(value: number, decimals: number): boolean {
  const scaled = value * 10 ** decimals;
  return Math.abs(scaled - Math.round(scaled)) < 1e-6;
}
